/**
 * WebhookEmailSettings — surfaces the importer-AP's webhook URL + email
 * address so users can wire upstream sources. Includes a "test simulieren"
 * button that hits the email-in endpoint with a small fixture file.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconCopy,
  IconFlask,
  IconMail,
  IconPlugConnected,
  IconRefresh,
} from "@tabler/icons-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useState } from "react"
import { api } from "../../../api/client"
import { IntegrationTestModal } from "./IntegrationTestModal"

interface Props {
  apId: string
  emailAddressDisplay?: string
  TestModal?: React.ComponentType<{
    apId: string
    emailAddressDisplay?: string
    onClose: () => void
  }>
}

export function WebhookEmailSettings({
  apId,
  emailAddressDisplay,
  TestModal,
}: Props) {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [showIntegrationTest, setShowIntegrationTest] = useState(false)

  const { data: settings, isLoading } = useQuery({
    queryKey: ["ap-settings", apId],
    queryFn: () => api.apSettings(apId),
  })

  const copy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedKey(key)
      setTimeout(
        () => setCopiedKey((prev) => (prev === key ? null : prev)),
        1500,
      )
    } catch {
      // best-effort
    }
  }

  const rotateToken = async () => {
    await api.apSettingsRotateToken(apId)
    queryClient.invalidateQueries({ queryKey: ["ap-settings", apId] })
  }

  if (isLoading || !settings) {
    return (
      <div className="rounded-lg border border-grey4 bg-grey1 p-4 text-xs text-grey5">
        {translate("QMS/SUPPLIER/SETTINGS_LOADING")}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <SettingRow
        icon={<IconPlugConnected size={14} />}
        label={translate("QMS/SUPPLIER/SOURCE_WEBHOOK")}
        value={settings.webhook_url}
        copied={copiedKey === "webhook"}
        onCopy={() => copy("webhook", settings.webhook_url)}
        rightSlot={
          <button
            onClick={rotateToken}
            className="rounded p-1 text-grey5 hover:bg-grey3 hover:text-white"
            title={translate("QMS/SUPPLIER/TOKEN_ROTATE")}
          >
            <IconRefresh size={14} />
          </button>
        }
      />
      <SettingRow
        icon={<IconMail size={14} />}
        label={translate("QMS/SUPPLIER/SOURCE_EMAIL")}
        value={emailAddressDisplay ?? settings.email_address}
        copied={copiedKey === "email"}
        onCopy={() =>
          copy("email", emailAddressDisplay ?? settings.email_address)
        }
        helper={translate("QMS/SUPPLIER/EMAIL_HELPER")}
      />
      <div className="flex items-center gap-2">
        <button
          onClick={() => setShowIntegrationTest(true)}
          className="inline-flex items-center gap-1.5 rounded-md border border-grey4 px-3 py-1.5 text-xs text-grey5 hover:border-grey5 hover:text-white"
        >
          <IconFlask size={12} />
          {translate("QMS/SUPPLIER/TEST_INTEGRATION")}
        </button>
      </div>

      {showIntegrationTest &&
        (() => {
          const Modal = TestModal ?? IntegrationTestModal
          return (
            <Modal
              apId={apId}
              emailAddressDisplay={emailAddressDisplay}
              onClose={() => setShowIntegrationTest(false)}
            />
          )
        })()}
    </div>
  )
}

function SettingRow({
  icon,
  label,
  value,
  copied,
  onCopy,
  helper,
  rightSlot,
}: {
  icon: React.ReactNode
  label: string
  value: string
  copied: boolean
  onCopy: () => void
  helper?: string
  rightSlot?: React.ReactNode
}) {
  const { translate } = useTranslation()
  return (
    <div className="rounded-lg border border-grey4 bg-grey1 p-3">
      <div className="flex items-center gap-2">
        <span className="text-grey5">{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wide text-grey5">
          {label}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-2">
        <code
          className="font-mono flex-1 truncate text-xs text-white"
          title={value}
        >
          {value}
        </code>
        <button
          onClick={onCopy}
          className={clsx(
            "shrink-0 rounded p-1 hover:bg-grey3",
            copied ? "text-success" : "text-grey5 hover:text-white",
          )}
          title={copied ? translate("QMS/COMMON/COPIED") : translate("QMS/COMMON/COPY")}
        >
          <IconCopy size={14} />
        </button>
        {rightSlot}
      </div>
      {helper && <p className="mt-1.5 text-[11px] text-grey5">{helper}</p>}
    </div>
  )
}
